
"use client";

import { ChildProfileDialog } from "@/components/child-profile-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import type { Child } from "@/lib/types";
import { UserPlus, Edit, Trash2 } from "lucide-react";

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { UpgradePrompt } from "@/components/upgrade-prompt";

type ManageChildrenProps = {
    children: Child[];
    onSave: (child: Omit<Child, 'id' | 'userId'> | Child) => void;
    onDelete: (childId: string) => void;
    isSubscribed?: boolean;
    maxChildren?: number;
}

const DEFAULT_MAX_FREE_CHILDREN = 2;

export function ManageChildren({ children, onSave, onDelete, isSubscribed = false, maxChildren = DEFAULT_MAX_FREE_CHILDREN }: ManageChildrenProps) {
    const canAddChild = isSubscribed || children.length < maxChildren;
    const atChildLimit = !isSubscribed && children.length >= maxChildren;
    return (
        <Card>
            <CardHeader>
                <div className="flex justify-between items-center">
                    <div>
                        <CardTitle>Manage Children</CardTitle>
                        <CardDescription>
                            {isSubscribed
                                ? 'Add, edit, or remove child profiles.'
                                : `Add up to ${maxChildren} children. Subscribe for unlimited.`}
                        </CardDescription>
                    </div>
                    <ChildProfileDialog onSave={onSave} child={null}>
                        <Button size="sm" disabled={!canAddChild}>
                            <UserPlus className="mr-2 h-4 w-4" />
                            Add Child
                        </Button>
                    </ChildProfileDialog>
                </div>
            </CardHeader>
            <CardContent>
                {atChildLimit && (
                    <div className="mb-4">
                        <UpgradePrompt message={`You've reached the limit of ${maxChildren} children`} />
                    </div>
                )}
                <div className="space-y-4">
                    {children.length > 0 ? children.map(child => (
                        <div key={child.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                            <div>
                                <p className="font-medium">{child.name}</p>
                                <p className="text-sm text-muted-foreground capitalize">{child.gender === 'other' ? 'Child' : child.gender}, born {child.birthMonth}/{child.birthYear}</p>
                            </div>
                            <div className="flex gap-2">
                                <ChildProfileDialog onSave={onSave} child={child}>
                                    <Button variant="ghost" size="icon">
                                        <Edit className="h-4 w-4" />
                                    </Button>
                                </ChildProfileDialog>

                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" aria-label={`Delete ${child.name}`}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                This will permanently delete {child.name}'s profile and all associated sleep data. This action cannot be undone.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction onClick={() => onDelete(child.id)}>Delete</AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        </div>
                    )) : (
                        <p className="text-muted-foreground text-center py-4">No children profiles found.</p>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}
